import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
var _excluded = ["style", "children", "delta", "priority", "applyZIndex", "coverChildren", "createStackingContext", "wrapperRef", "useWrapper"];

var _class, _class2, _temp;

import React from 'react';
import { globalObject, isBrowser } from '@skbkontur/global-object';
import { callChildRef } from "../../../lib/callChildRef/callChildRef";
import { rootNode } from "../../../lib/rootNode";
import { createPropsGetter } from "../../../lib/createPropsGetter";
import { isInstanceOf } from "../../../lib/isInstanceOf";
import { LoaderDataTids } from "../../../components/Loader";
import { PORTAL_INLET_ATTR, PORTAL_OUTLET_ATTR } from "../../RenderContainer";
import { incrementZIndex, removeZIndex, upperBorder } from "../ZIndexStorage";
var DEFAULT_ZINDEX_CONTEXT = {
  parentLayerZIndex: 0,
  maxZIndex: Infinity
};
var ZIndexContext = /*#__PURE__*/React.createContext(DEFAULT_ZINDEX_CONTEXT);
ZIndexContext.displayName = 'ZIndexContext';
export var ZIndex = rootNode(_class = (_temp = _class2 = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(ZIndex, _React$Component);

  function ZIndex(props) {
    var _this;

    _this = _React$Component.call(this, props) || this;
    _this.state = {
      zIndex: 0,
      savedZIndexContext: null
    };
    _this.getProps = createPropsGetter(ZIndex.defaultProps);
    _this.zIndexContext = null;

    _this.wrapperRef = function (element) {
      var wrapperRef = _this.props.wrapperRef;

      _this.setRootNode(element);

      wrapperRef && callChildRef(wrapperRef, element);
      element && _this.tryGetContextByDOM(element);
    };

    _this.increment = function () {
      var _this$getProps = _this.getProps(),
          priority = _this$getProps.priority,
          delta = _this$getProps.delta;

      return incrementZIndex(priority, delta);
    };

    _this.tryGetContextByDOM = function (element) {
      if (DEFAULT_ZINDEX_CONTEXT === _this.zIndexContext && _this.state.savedZIndexContext === null) {
        var _element$parentElemen;

        var savedZIndexContext = DEFAULT_ZINDEX_CONTEXT;
        var portal = (_element$parentElemen = element.parentElement) == null ? void 0 : _element$parentElemen.closest("[" + PORTAL_OUTLET_ATTR + "]");

        if (isInstanceOf(portal, globalObject.HTMLElement)) {
          var _noscript$parentEleme;

          var portalID = portal.getAttribute(PORTAL_OUTLET_ATTR);
          var noscript = document.querySelector("noscript[" + PORTAL_INLET_ATTR + "=\"" + portalID + "\"]");
          var parent = noscript == null ? void 0 : (_noscript$parentEleme = noscript.parentElement) == null ? void 0 : _noscript$parentEleme.closest('[style*=z-index]');

          if (isInstanceOf(parent, globalObject.HTMLElement)) {
            var _parent$parentElement;

            var newZIndex = Number(parent.style.zIndex || 0);
            var maxZIndex = Infinity;

            if (((_parent$parentElement = parent.parentElement) == null ? void 0 : _parent$parentElement.dataset.tid) === LoaderDataTids.veil) {
              maxZIndex = _this.calcZIndex(newZIndex, maxZIndex);
            }

            savedZIndexContext = {
              maxZIndex: maxZIndex,
              parentLayerZIndex: newZIndex
            };
          }
        }

        _this.setState({
          savedZIndexContext: savedZIndexContext
        });
      }
    };

    _this.state.zIndex = _this.increment();
    return _this;
  }

  var _proto = ZIndex.prototype;

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (prevProps.priority !== this.props.priority || prevProps.delta !== this.props.delta) {
      removeZIndex(this.state.zIndex);
      this.setState({
        zIndex: this.increment()
      });
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    removeZIndex(this.state.zIndex);
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$getProps2 = this.getProps(),
        style = _this$getProps2.style,
        children = _this$getProps2.children,
        delta = _this$getProps2.delta,
        priority = _this$getProps2.priority,
        applyZIndex = _this$getProps2.applyZIndex,
        coverChildren = _this$getProps2.coverChildren,
        createStackingContext = _this$getProps2.createStackingContext,
        wrapperRef = _this$getProps2.wrapperRef,
        useWrapper = _this$getProps2.useWrapper,
        rest = _objectWithoutPropertiesLoose(_this$getProps2, _excluded);

    var wrapperStyle = {};
    return /*#__PURE__*/React.createElement(ZIndexContext.Consumer, null, function (context) {
      _this2.zIndexContext = context;

      var _ref = _this2.state.savedZIndexContext || context,
          parentLayerZIndex = _ref.parentLayerZIndex,
          maxZIndex = _ref.maxZIndex;

      var zIndexContextValue = {
        parentLayerZIndex: parentLayerZIndex,
        maxZIndex: maxZIndex
      };
      var newZIndex = 0;

      if (applyZIndex) {
        newZIndex = _this2.calcZIndex(parentLayerZIndex, maxZIndex);
        wrapperStyle.zIndex = newZIndex;
        zIndexContextValue = coverChildren ? {
          parentLayerZIndex: parentLayerZIndex,
          maxZIndex: newZIndex
        } : {
          parentLayerZIndex: newZIndex,
          maxZIndex: Number.isFinite(maxZIndex) ? newZIndex : Infinity
        };

        if (createStackingContext) {
          isBrowser(globalObject) && 'isolation' in globalObject.document.body.style ? wrapperStyle.isolation = 'isolate' : wrapperStyle.transform = 'rotate(0)';
        }
      }

      var child = !useWrapper ? children : /*#__PURE__*/React.createElement("div", _extends({
        style: _extends({}, style, wrapperStyle),
        ref: _this2.wrapperRef
      }, rest), children);
      return /*#__PURE__*/React.createElement(ZIndexContext.Provider, {
        value: zIndexContextValue
      }, child);
    });
  };

  _proto.calcZIndex = function calcZIndex(parentLayerZIndex, maxZIndex) {
    var newZIndex = this.state.zIndex;

    if (Number.isFinite(maxZIndex)) {
      var allowedValuesIntervalLength = maxZIndex - parentLayerZIndex;
      var scale = upperBorder / allowedValuesIntervalLength;
      newZIndex = Math.ceil(newZIndex / scale);
    }

    newZIndex += parentLayerZIndex;
    return newZIndex;
  };

  return ZIndex;
}(React.Component), _class2.__KONTUR_REACT_UI__ = 'ZIndex', _class2.displayName = 'ZIndex', _class2.defaultProps = {
  delta: 10,
  priority: 0,
  style: {},
  applyZIndex: true,
  coverChildren: false,
  createStackingContext: false,
  useWrapper: true
}, _class2.propTypes = {
  delta: function delta(props) {
    if ((props.delta || _class2.defaultProps.delta) <= 0) {
      return new Error("[ZIndex]: Prop 'delta' must be greater than 0, received " + props.delta);
    }

    if (Math.trunc(props.delta || _class2.defaultProps.delta) !== props.delta) {
      return new Error("[ZIndex]: Prop 'delta' must be integer, received " + props.delta);
    }
  }
}, _temp)) || _class;